import axios from 'axios';
import { currentEnvConfig } from '@/config/environmentsConfig';
import useApi from './api';
import useUserStore from '@/stores/user';
import { triggerBrowserDownload, parseContentDispositionFilename } from '@/helpers/utils';

// Helper function to get JWT token from localStorage
function getJwtToken(): string | null {
  try {
    const userFromLocalStorage = localStorage.getItem('user');
    if (userFromLocalStorage) {
      const user = JSON.parse(userFromLocalStorage);
      return user.jwtToken || null;
    }
  } catch (error) {
    // Silently fail if localStorage is unavailable or corrupted
    console.error('Error getting JWT token from localStorage', error);
  }
  return null;
}

// Helper function to get Authorization header with JWT token
function getAuthHeaders(): Record<string, string> {
  const token = getJwtToken();
  if (token) {
    return {
      'Authorization': `Bearer ${token}`
    };
  }
  return {};
}

// Helper function to check if an error is a 401 Unauthorized
function isUnauthorizedError(error: any): boolean {
  return (
    error.response?.status === 401 ||
    error.status === 401 ||
    (error.message && (
      error.message.includes('401') ||
      error.message.toLowerCase().includes('unauthorized') ||
      error.message.toLowerCase().includes('token expired')
    ))
  );
}

// Helper function to refresh JWT token using the dedicated refresh endpoint
// Requires FormsAuthentication session cookie (withCredentials: true)
async function refreshJwtToken(): Promise<string | null> {
  try {
    const { post } = useApi();
    
    // POST to /Api/Auth/RefreshToken endpoint
    // withCredentials is already set globally in api.ts (axios.defaults.withCredentials = true)
    const { responseBody, response } = await post('Api/Auth/RefreshToken', {}, {
      isLogoutOnInvalidAuth: false // Don't logout on invalid auth for token refresh
    });
    
    // Response is JSON: { "token": "..." }
    if (response.status === 401) {
      // User not authenticated - token refresh failed
      console.error('User not authenticated - token refresh failed');
      return null;
    }
    
    if (responseBody?.token) {
      const newToken = responseBody.token;
      
      // Update token in store and localStorage
      const userStore = useUserStore();
      userStore.jwtToken = newToken;
      
      const userFromLocalStorage = localStorage.getItem('user');
      if (userFromLocalStorage) {
        const user = JSON.parse(userFromLocalStorage);
        user.jwtToken = newToken;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return newToken;
    }
  } catch (error: any) {
    // Handle 401 or other errors
    if (error.response?.status === 401) {
      return null;
    }
  }
  return null;
}

// Wrapper that handles auth retry and common error handling for all metrics API calls.
// On 401, attempts a token refresh and retries the request once before failing.
async function withAuthRetry<T>(fn: () => Promise<T>, fallbackMessage: string): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    if (isUnauthorizedError(error)) {
      const newToken = await refreshJwtToken();
      if (newToken) {
        return await fn();
      }
      throw new Error('Your session has expired. Please refresh the page to continue.');
    }

    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(`Cannot connect to metrics API at ${METRICS_BASE_URL}. Please ensure the API server is running.`);
    }

    if (error.code === 'ECONNABORTED') {
      throw new Error('Metrics API request timed out. Please try again or refresh the page.');
    }

    if (error.response?.status === 400) {
      const errorMsg = error.response?.data?.message || 'Invalid request parameters.';
      throw new Error(`API validation error: ${errorMsg}`);
    }

    if (error.response?.status >= 500) {
      throw new Error('Metrics API server error. Please try again later.');
    }

    throw new Error(error?.message || fallbackMessage);
  }
}

// Currently playing content interface
export interface CurrentlyPlayingContent {
  type: string;
  feature: string;
  contentUrl: string;
  contentTitle: string;
}

// Proof of Play media item (from /api/v1/metrics/proof-of-play byMedia)
export interface AlreadyPlayedContent {
  media_id: string;
  media_name: string;
  media_url: string;
  total_plays: number;
  total_skips: number;
  player_count: number;
  play_rate: number;
}

// Legacy raw metrics interface (keeping for backward compatibility)
export interface MetricsData {
  id: string;
  player_uuid: string;
  account_id: number;
  event_type: string;
  mem_free_mb: number;
  mem_total_mb: number;
  cpu_usage_pct: number;
  cpu_count: number;
  disk_free_mb: number;
  disk_total_mb: number;
  temperature_c: number;
  currently_playing: CurrentlyPlayingContent[];
  already_played?: AlreadyPlayedContent[];
  created_at: string;
  sent_at: string;
}

// New aggregated metrics interface
export interface AggregatedMetricsData {
  time_bucket: string;
  player_uuid: string;
  avg_mem_usage_pct: number;
  avg_cpu_usage_pct: number;
  avg_disk_usage_pct: number;
  avg_temperature_c: number;
  max_mem_usage_pct: number;
  max_cpu_usage_pct: number;
  max_disk_usage_pct: number;
  max_temperature_c: number;
  data_points: number;
}

export interface AggregatedMetricsResponse {
  success: boolean;
  data: AggregatedMetricsData[];
  interval: string;
  totalBuckets: number;
  filters: {
    accountId: number;
    startTime: string;
    endTime: string;
    interval: string;
  };
}

export interface MetricsQueryParams {
  accountId?: number; // Primary filter - account ID
  startTime?: string; // Time range start
  endTime?: string;   // Time range end
  limit?: number;     // Pagination limit
  offset?: number;    // Pagination offset
  // Note: playerUuid filtering removed as per dashboard requirements
}

export interface AggregatedMetricsQueryParams {
  accountId: number;      // Required: Account ID to filter data
  startTime: string;      // Required: Start time in ISO 8601 format
  endTime: string;        // Required: End time in ISO 8601 format
  interval?: '1h' | '4h' | '6h' | '1d'; // Optional: Time bucket interval
  playerUuids?: string;   // Optional: Comma-separated list of player UUIDs
}

const METRICS_BASE_URL = currentEnvConfig?.metricsBaseUrl;

// Proof of Play - per-player totals
export interface ProofOfPlayByPlayer {
  player_uuid: string;
  total_plays: number;
  total_skips: number;
  unique_media_count: number;
  play_rate: number;
}

// Proof of Play - time series bucket
export interface ProofOfPlayTimeSeries {
  bucket: string;
  total_plays: number;
  total_skips: number;
  unique_media_count: number;
  data_points: number;
}

// Proof of Play - summary totals
export interface ProofOfPlaySummary {
  total_plays: number;
  total_skips: number;
  unique_media_count: number;
  unique_player_count: number;
  record_count: number;
  play_rate: number;
}

// Proof of Play - resolved time range
export interface ProofOfPlayTimeRangeInfo {
  startTime: string;
  endTime: string;
  preset: string;
  label: string;
}

// Proof of Play - filter echo
export interface ProofOfPlayFilters {
  accountId: number | null;
  playerUuids: string | null;
  mediaIds: string | null;
}

// Proof of Play - pagination info per section
export interface ProofOfPlayPaginationInfo {
  total: number;
  offset: number;
}

// Full Proof of Play response
export interface ProofOfPlayMetricsResponse {
  success: boolean;
  byMedia: AlreadyPlayedContent[];
  byPlayer: ProofOfPlayByPlayer[];
  timeSeries: ProofOfPlayTimeSeries[];
  summary: ProofOfPlaySummary;
  timeRange: ProofOfPlayTimeRangeInfo;
  filters: ProofOfPlayFilters;
  pagination: {
    byMedia: ProofOfPlayPaginationInfo;
    byPlayer: ProofOfPlayPaginationInfo;
  };
}

export type ProofOfPlayTimeRangePreset = '1min' | '1h' | '12h' | '24h' | 'today' | 'yesterday' | '1w' | '1m' | 'quarter' | 'ytd';

export interface ProofOfPlayMetricsQueryParams {
  accountId?: number;
  timeRange?: ProofOfPlayTimeRangePreset;
  playerUuids?: string;
  mediaIds?: string;
  mediaLimit?: number;
  mediaOffset?: number;
  playerLimit?: number;
  playerOffset?: number;
}

const EMPTY_PROOF_OF_PLAY_RESPONSE: ProofOfPlayMetricsResponse = {
  success: false,
  byMedia: [],
  byPlayer: [],
  timeSeries: [],
  summary: { total_plays: 0, total_skips: 0, unique_media_count: 0, unique_player_count: 0, record_count: 0, play_rate: 0 },
  timeRange: { startTime: '', endTime: '', preset: '', label: '' },
  filters: { accountId: null, playerUuids: null, mediaIds: null },
  pagination: {
    byMedia: { total: 0, offset: 0 },
    byPlayer: { total: 0, offset: 0 },
  },
};

export async function getProofOfPlayMetrics(params: ProofOfPlayMetricsQueryParams): Promise<ProofOfPlayMetricsResponse> {
  function normalizeResponse(data: Partial<ProofOfPlayMetricsResponse>): ProofOfPlayMetricsResponse {
    if (!data || !data.success) {
      return EMPTY_PROOF_OF_PLAY_RESPONSE;
    }
    return {
      success: true,
      byMedia: Array.isArray(data.byMedia) ? data.byMedia : [],
      byPlayer: Array.isArray(data.byPlayer) ? data.byPlayer : [],
      timeSeries: Array.isArray(data.timeSeries) ? data.timeSeries : [],
      summary: data.summary || EMPTY_PROOF_OF_PLAY_RESPONSE.summary,
      timeRange: data.timeRange || EMPTY_PROOF_OF_PLAY_RESPONSE.timeRange,
      filters: data.filters || EMPTY_PROOF_OF_PLAY_RESPONSE.filters,
      pagination: data.pagination || EMPTY_PROOF_OF_PLAY_RESPONSE.pagination,
    };
  }

  return withAuthRetry(async () => {
    const response = await axios.get<Partial<ProofOfPlayMetricsResponse>>(`${METRICS_BASE_URL}/api/v1/metrics/proof-of-play`, {
      params,
      headers: getAuthHeaders(),
      withCredentials: false,
      timeout: 15000
    });
    return normalizeResponse(response.data);
  }, 'Failed to fetch proof of play metrics.');
}

// Sections the export endpoint can render. Backend returns one XLSX sheet per
// section; csv-join when sending so backend can also restrict to a subset.
export type ProofOfPlayExportSection = 'summary' | 'byMedia' | 'byPlayer' | 'timeSeries';

export interface ProofOfPlayExportParams {
  accountId?: number;
  timeRange?: ProofOfPlayTimeRangePreset;
  playerUuids?: string;
  mediaIds?: string;
  sections?: string; // csv of ProofOfPlayExportSection values
}

/**
 * Triggers a browser download of the streamed XLSX produced by
 * `GET /api/v1/metrics/proof-of-play/export`.
 *
 * Replaces the previous client-side workbook build (`xlsx` npm package).
 * Backend owns the layout and streams the file directly, so accounts with
 * millions of plays no longer freeze the tab or get silently truncated.
 *
 * Filename is taken from the response's Content-Disposition header when
 * present; falls back to a sensible local name.
 */
export async function downloadProofOfPlayExport(params: ProofOfPlayExportParams): Promise<void> {
  return withAuthRetry(async () => {
    const response = await axios.get<Blob>(
      `${METRICS_BASE_URL}/api/v1/metrics/proof-of-play/export`,
      {
        params,
        headers: getAuthHeaders(),
        withCredentials: false,
        responseType: 'blob',
        // Exports for large accounts can legitimately take 30s+; backend
        // streaming is verified up to ~16s for 5M source rows. Pick a
        // generous ceiling but still bounded.
        timeout: 120000,
      },
    );
    const today = new Date().toISOString().slice(0, 10);
    const filename =
      parseContentDispositionFilename(response.headers['content-disposition'])
      ?? `proof-of-play-${params.timeRange ?? 'export'}-${today}.xlsx`;
    triggerBrowserDownload(response.data, filename);
  }, 'Failed to download proof of play export.');
}

export async function getMetrics(params: MetricsQueryParams): Promise<MetricsData[]> {
  return withAuthRetry(async () => {
    const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/query`, {
      params,
      headers: getAuthHeaders(),
      withCredentials: false,
      timeout: 10000
    });

    if (!response.data || !response.data.success || !response.data.data) {
      return [];
    }

    return Array.isArray(response.data.data) ? response.data.data : [];
  }, 'Failed to fetch metrics data. Please refresh the page and try again.');
}

export async function getLatestMetrics(params: Pick<MetricsQueryParams, 'accountId' | 'limit'>): Promise<MetricsData[]> {
  return withAuthRetry(async () => {
    const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/latest`, {
      params,
      headers: getAuthHeaders(),
      withCredentials: false,
      timeout: 10000
    });

    if (!response.data || !response.data.success || !response.data.data) {
      return [];
    }

    return Array.isArray(response.data.data) ? response.data.data : [];
  }, 'Failed to fetch latest metrics data. Please refresh the page and try again.');
}

// Fetch latest metrics for a specific player UUID (dashed format)
export async function getLatestMetricsByPlayerUuid(playerUuid: string, limit: number = 1): Promise<MetricsData[]> {
  return withAuthRetry(async () => {
    const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/latest`, {
      params: { playerUuid, limit },
      headers: getAuthHeaders(),
      withCredentials: false,
      timeout: 10000
    });

    if (!response.data || !response.data.success || !response.data.data) {
      return [];
    }

    return Array.isArray(response.data.data) ? response.data.data : [];
  }, 'Failed to fetch latest metrics for player. Please refresh the page and try again.');
}

// New optimized aggregated metrics API function
export async function getAggregatedMetrics(params: AggregatedMetricsQueryParams): Promise<AggregatedMetricsResponse> {
  return withAuthRetry(async () => {
    const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/aggregated`, {
      params,
      headers: getAuthHeaders(),
      withCredentials: false,
      timeout: 10000
    });

    if (!response.data || !response.data.success) {
      throw new Error('API returned unsuccessful response');
    }

    return response.data as AggregatedMetricsResponse;
  }, 'Failed to fetch aggregated metrics data. Please refresh the page and try again.');
}

// Helper function to determine optimal interval based on date range
export function getOptimalInterval(startDate: string, endDate: string): '1h' | '4h' | '6h' | '1d' {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const durationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  
  // Single-day and near-single-day: prefer 4-hour buckets
  if (durationHours <= 30) {
    return '4h';
  } else if (durationHours <= 48) {
    return '1h';   // Up to 2 days: hourly data
  } else if (durationHours <= 168) {
    return '6h';   // Last week: 6-hour data
  } else {
    return '1d';   // More than a week: daily data
  }
}

// Helper function to transform metrics data for heatmap visualization
// Takes account-filtered metrics and transforms them for visualization
// Each data point represents one metric reading from one device at one point in time
export function transformMetricsForHeatmap(
  metrics: MetricsData[], 
  metricType: 'mem' | 'cpu' | 'disk' | 'temp'
): Array<{
  timestamp: string;
  playerUuid: string;
  value: number;
}> {
  const transformed = metrics.map(metric => ({
    timestamp: metric.sent_at, // Use sent_at as timestamp
    playerUuid: metric.player_uuid, // Keep player_uuid - heatmap shows multiple devices
    value: getMetricValue(metric, metricType)
  }));
  
  return transformed;
}

// New transform function for aggregated metrics data
export function transformAggregatedMetricsForHeatmap(
  aggregatedMetrics: AggregatedMetricsData[], 
  metricType: 'mem' | 'cpu' | 'disk' | 'temp'
): Array<{
  timestamp: string;
  playerUuid: string;
  value: number;
}> {
  const transformed = aggregatedMetrics.map(metric => ({
    timestamp: metric.time_bucket, // Use time_bucket as timestamp
    playerUuid: metric.player_uuid, // Keep player_uuid - heatmap shows multiple devices
    value: getAggregatedMetricValue(metric, metricType)
  }));
  
  return transformed;
}

function getMetricValue(metric: MetricsData, type: 'mem' | 'cpu' | 'disk' | 'temp'): number {
  switch (type) {
    case 'mem': {
      // Calculate memory usage percentage: (total - free) / total * 100
      const memUsed = metric.mem_total_mb - metric.mem_free_mb;
      return (memUsed / metric.mem_total_mb) * 100;
    }
    case 'cpu':
      return metric.cpu_usage_pct;
    case 'disk': {
      // Calculate disk usage percentage: (total - free) / total * 100
      const diskUsed = metric.disk_total_mb - metric.disk_free_mb;
      return (diskUsed / metric.disk_total_mb) * 100;
    }
    case 'temp':
      return metric.temperature_c;
    default:
      return 0;
  }
}

function getAggregatedMetricValue(metric: AggregatedMetricsData, type: 'mem' | 'cpu' | 'disk' | 'temp'): number {
  switch (type) {
    case 'mem':
      return metric.avg_mem_usage_pct;
    case 'cpu':
      return metric.avg_cpu_usage_pct;
    case 'disk':
      return metric.avg_disk_usage_pct;
    case 'temp':
      return metric.avg_temperature_c;
    default:
      return 0;
  }
}
